import { useQuery, useInfiniteQuery } from "@tanstack/react-query"
import {
  illustrationLogs,
  getAllIllustrationResults,
  getIllustrationLogsByAccessionNo,
} from "../api/illustrationLogs"

const useIllustrationLogs = ({ accessionNo, logType, inboundMessageId }) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["illustrationLogs", accessionNo, logType, inboundMessageId],
    queryFn: () => illustrationLogs({ accessionNo, logType, inboundMessageId }),
  })
  return {
    data,
    isLoading,
    isError,
    error,
  }
}

export const useAllIllustrationLogs = ({ limit = 20, sortBy, filters }) =>
  useInfiniteQuery({
    queryKey: ["allIllustrationLogs", "infinite", sortBy, filters],
    queryFn: ({ pageParam = 0 }) =>
      getAllIllustrationResults({
        offset: pageParam,
        limit,
        sortBy,
        ...filters,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce(
        (sum, page) => sum + page.data.data.length,
        0
      )

      return lastPage.data.data.length === limit ? loadedItems : undefined
    },
    initialPageParam: 0,
    retry: 0,
    staleTime: 0,
    refetchInterval: 1 * 60 * 1000,
    select: (data) => {
      const lastPage = data?.pages?.[data?.pages?.length - 1]?.data
      const flattenedData = data?.pages?.flatMap((page) => page.data.data) || []

      return {
        data: flattenedData,
        total: lastPage?.total || 0,
        page: lastPage?.page || 1,
        limit: lastPage?.limit || limit,
        totalPages: lastPage?.totalPages || 1,
      }
    },
  })

export const useIllustrationLogsByAccessionNo = ({
  messageId,
  accessionNo,
  source,
  ov3Id,
  bodyPart,
}) =>
  useQuery({
    queryKey: [
      "illustrationLogsByAccessionNo",
      messageId,
      accessionNo,
      source,
      ov3Id,
      bodyPart,
    ],
    queryFn: () =>
      getIllustrationLogsByAccessionNo({
        messageId,
        accessionNo,
        source,
        ov3Id,
        bodyPart,
      }),
    retry: 0,
    enabled:
      !!accessionNo && !!source && !!bodyPart && (!!messageId || !!ov3Id),
    select: (data) => data?.data || [],
  })

export default useIllustrationLogs
